import { Chip } from '@ds';
import { CaseStatus } from '@/api/apis/case';
import {
  CaseNotificationData,
  NotificationDataStatus,
  NotificationToastMessageType,
  NotificationToastType,
} from '@/api/apis/communication';
import { caseStatusNames } from '@/constants/case/case.constants';
import { getColorByStatus } from './case.helpers';

// TODO: This function accepts a broader status union, but currently only supports CaseStatus.
export const getOldAndNewStatus = (
  OldStatus: NotificationDataStatus,
  NewStatus: NotificationDataStatus,
  body?: string,
) => {
  if (OldStatus === undefined || NewStatus === undefined) {
    return (
      <div className="flex flex-row items-center gap-1">
        <span className="text-s-r text-base-5">{body}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-row items-center gap-1">
      <Chip size="sm" className={getColorByStatus(OldStatus as CaseStatus)}>
        <div className="text-s-sb">{caseStatusNames[OldStatus as CaseStatus]}</div>
      </Chip>

      <span className="text-s-r text-base-5">changed to</span>

      <Chip size="sm" className={getColorByStatus(NewStatus as CaseStatus)}>
        <div className="text-s-sb">{caseStatusNames[NewStatus as CaseStatus]}</div>
      </Chip>
    </div>
  );
};

export const notificationBody = (body: string) => {
  return (
    <div className="flex flex-row items-center gap-1">
      <span className="text-s-r text-base-5">{body}</span>
    </div>
  );
};

export type ToastNotificationType = {
  body: string;
  isNopConnectPlatform?: boolean;
  notification: NotificationToastType;
};

export const jsonArrayHasItems = (jsonArrayString: string | undefined) => {
  try {
    const parsedArray = JSON.parse(jsonArrayString || '[]');

    return Array.isArray(parsedArray) && parsedArray.length > 0;
  } catch {
    return false;
  }
};

export const getBodyForToastNotification = ({ body, isNopConnectPlatform, notification }: ToastNotificationType) => {
  const { toastType, data } = notification;
  const { OldStatus, NewStatus, txcCode } = data ?? {};

  switch (toastType) {
    case 'OrganApprovalUpdated': {
      return notificationBody(body);
    }

    case 'CaseQuoteReceivedByTxCenter': {
      if (isNopConnectPlatform) {
        return notificationBody(`Logistics quote sent to ${txcCode} for APPROVAL`);
      }

      return notificationBody(body);
    }

    case 'OrganAcceptanceCriteriaUpdated':
      return notificationBody('Organ acceptance criteria updated');

    case 'CaseStatusUpdated': {
      if (isNopConnectPlatform) {
        if (OldStatus === 'Draft' && NewStatus === 'ClinicalScreening') {
          return notificationBody(`Case has been INITIATED, TransMedics, with ${NewStatus}`);
        }

        if (OldStatus === 'TX Center Draft' && NewStatus === 'Clinical Screening') {
          return notificationBody(`Case Created in status: ${NewStatus}`);
        }
      }

      return notificationBody(body);
    }

    case 'CaseCreated':
    case 'CaseResourceAssigned':
    case 'CaseResourceUnassigned':
    case 'OrganApprovalAccepted':
    case 'OrganApprovalRejected':
    case 'CaseDetailsUpdated':
    case 'NOPServiceApproval':
    case 'UpdateOrganAcceptanceCriteria':
    case 'CaseResourceReserved':
    case 'CaseReviewUpdated':
    case 'CaseRouteUpdated':
    case 'CaseQuoteRejectedInternally':
    case 'CaseQuoteCreated':
    case 'CaseQuoteApprovedByTxCenter':
    case 'CaseQuoteRejectedByTxCenter':
    case 'CaseQuoteRecalledByTxCenter':
    case 'CaseQuoteApprovedInternally':
    case 'CaseQuoteApprovedTxCenterProvider':
    case 'CaseResourceInvitationDeclined':
    case 'ResourceStatusUpdated':
    case 'CaseQuoteSentToTxCenter':
    case 'RandomizationCompleted':
    case 'RandomizationReady':
    case 'TrialCaseUpdated':
    case 'TBD':
    case 'UnallocatedClinicalRequestReviewNeeded':
      return notificationBody(body);

    case 'MAOTripCreationFailed':
      return notificationBody('Trip is not created in MAO');

    case 'RoutePlanningStatusUpdated':
      switch (NewStatus) {
        case 'PendingResourcesBooking':
          return notificationBody('Please add details on booked vehicles to all ground legs');

        case 'WaitingForAviationApproval':
          return notificationBody(body);

        default:
          return notificationBody(body);
      }

    default:
      return notificationBody(body);
  }
};

export type ToastType = {
  notificationType?: NotificationToastMessageType;
  data?: CaseNotificationData;
  body?: string;
};

const normalizeNotificationStatus = (status?: NotificationDataStatus): string => {
  return String(status ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');
};

const getStatusFromNotificationBody = (body?: string): 'success' | 'error' | undefined => {
  const normalizedBody = (body ?? '').toLowerCase();

  if (/\b(confirmed|approved|accepted)\b/.test(normalizedBody)) {
    return 'success';
  }

  if (/\b(declined|rejected)\b/.test(normalizedBody)) {
    return 'error';
  }

  return undefined;
};

const getCaseReviewUpdatedToastType = <TFallback extends 'success' | 'error' | 'warning' | 'info'>(
  newStatus?: NotificationDataStatus,
  fallback: TFallback = 'warning' as TFallback,
  body?: string,
): 'success' | 'error' | TFallback => {
  const normalizedStatus = normalizeNotificationStatus(newStatus);

  switch (normalizedStatus) {
    case 'confirmed':
    case 'approved':
    case 'accepted':
      return 'success';

    case 'declined':
    case 'rejected':
      return 'error';

    default:
      // Some backend event sources (e.g. TxCenterServiceApproved/Rejected) don't populate
      // NewStatus at all, so fall back to reading the outcome out of the notification text.
      return getStatusFromNotificationBody(body) ?? fallback;
  }
};

export const getTypeForToastNotification = ({ notificationType, data, body }: ToastType) => {
  if (!notificationType) {
    return 'info';
  }

  const { NewStatus, FailedCases, FailedCasesWithCertificationReason } = data ?? {};

  switch (notificationType) {
    case 'CaseCreated':
    case 'CaseResourceAssigned':
    case 'CaseResourceUnassigned':
    case 'OrganAcceptanceCriteriaUpdated':
    case 'OrganApprovalUpdated':
    case 'OrganApprovalAccepted':
    case 'OrganApprovalRejected':
    case 'CaseDetailsUpdated':
    case 'CaseQuoteReceivedByTxCenter':
    case 'ResourceStatusUpdated':
    case 'UpdateOrganAcceptanceCriteria':
    case 'CaseResourceReserved':
    case 'CaseQuoteCreated':
    case 'CaseQuoteSentToTxCenter':
    case 'UnallocatedClinicalRequestReviewNeeded':
      return 'info';

    case 'TBD':
    case 'MAOTripCreationFailed':
    case 'CaseQuoteRejectedInternally':
    case 'CaseQuoteRejectedByTxCenter':
      return 'error';

    case 'NOPServiceApproval':
    case 'CaseQuoteApprovedByTxCenter':
    case 'CaseQuoteApprovedInternally':
    case 'CaseQuoteApprovedTxCenterProvider':
      return 'success';

    case 'CaseReviewUpdated':
    case 'CaseResourceInvitationDeclined':
      return getCaseReviewUpdatedToastType(NewStatus, 'warning', body);

    case 'CaseRouteUpdated':
      return getCaseReviewUpdatedToastType(NewStatus, 'info', body);

    case 'RoutePlanningStatusUpdated':
      switch (NewStatus) {
        case 'PendingResourcesBooking':
        case 'WaitingForAviationApproval':
          return 'warning';

        default:
          return 'info';
      }

    case 'CaseStatusUpdated':
      switch (NewStatus) {
        case 'ClinicalScreening':
        case 'ResourceDeployed':
        case 'Finished':
        case 'PendingLogistics':
        case 'ResourcesBooked':
          return 'info';

        case 'Declined':
        case 'TransplantNotCompleted':
          return 'warning';

        case 'OrganOnOCS':
          return 'success';

        default:
          return 'info';
      }

    case NotificationToastMessageType.CaseBulkOperationPrimaryElevation:
    case NotificationToastMessageType.CaseBulkSwapOperationCompleted:
    case NotificationToastMessageType.CaseBulkAssignOperationCompleted:
    case NotificationToastMessageType.CaseBulkDetachOperationCompleted:
      if (jsonArrayHasItems(FailedCases) || jsonArrayHasItems(FailedCasesWithCertificationReason)) {
        return 'error';
      }

      return 'success';

    default:
      return 'info';
  }
};

export const notificationAvailable = (): boolean => {
  return typeof Notification !== 'undefined' && typeof window !== 'undefined' && window.top === window.self;
};

/**
 * Utility function to check browser notification permission and log if disabled.
 *
 * @param source - The source of the notification, for example GetStream or WebSocket.
 * @param notificationData - Optional data about the notification for logging.
 */
export const checkAndLogNotificationPermission = (source: string, notificationData?: Record<string, unknown>) => {
  if (typeof Notification === 'undefined') {
    console.log(`🚫 ${source} notification blocked - Notifications not supported in this browser`);

    return false;
  }

  const logData = {
    source,
    permission: Notification.permission,
    timestamp: new Date().toISOString(),
    ...notificationData,
  };

  switch (Notification.permission) {
    case 'denied':
      console.log(`🚫 ${source} notification blocked - Browser notifications disabled:`, JSON.stringify(logData));

      return false;

    case 'default':
      console.log(`⚠️ ${source} notification - Permission not requested yet:`, JSON.stringify(logData));

      return false;

    case 'granted':
      return true;

    default:
      return false;
  }
};
